import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import { ArrowBack, CalendarToday, Category, Person } from '@mui/icons-material';
import { resourcesAPI } from '../services/api';

interface Resource {
  id: string;
  title: string;
  description: string;
  content: string;
  slug: string;
  featuredImage?: {
    url: string;
    alt: string;
  };
  category: string;
  tags: string[];
  publishedDate: string;
  updatedDate: string;
}

const ResourceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [resource, setResource] = useState<Resource | null>(null);
  const [recommendedResources, setRecommendedResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchResource(id);
    }
  }, [id]);

  const fetchResource = async (resourceId: string) => {
    try {
      setLoading(true);
      const response = await resourcesAPI.getById(resourceId);
      if (response.data.success) {
        setResource(response.data.data);
        fetchRecommended(resourceId);
      } else {
        setError('Resource not found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch resource');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommended = async (currentId: string) => {
    try {
      const response = await resourcesAPI.getAll();
      if (response.data.success && Array.isArray(response.data.data)) {
        const others: Resource[] = response.data.data
          .filter((item: Resource) => item.id !== currentId)
          .sort((a: Resource, b: Resource) =>
            new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
          )
          .slice(0, 3);
        setRecommendedResources(others);
      }
    } catch (e) {
      // Silently ignore recommended fetch errors
      console.error('Failed to fetch recommended resources', e);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBack = () => {
    navigate('/resources');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !resource) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Resource not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box className="resource-detail-page" sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container className="resource-detail__container" maxWidth="lg" sx={{ pt: 2, pb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs className="resource-detail__breadcrumbs" sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleBack}
            sx={{ textDecoration: 'none' }}
          >
            Resources
          </Link>
          <Typography color="text.primary" variant="body2">
            {resource.title}
          </Typography>
        </Breadcrumbs>

        {/* Main Content */}
        <Paper className="resource-detail__content" elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}>
          {/* Header */}
          <Box className="resource-detail__header" mb={4}>
            <Typography className="resource-detail__title" variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              {resource.title}
            </Typography>
            
            <Typography className="resource-detail__description" variant="h6" color="text.secondary" paragraph sx={{ mb: 3 }}>
              {resource.description}
            </Typography>

            {/* Meta Information */}
            <Box className="resource-detail__meta" display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={3}>
              {resource.category && (
                <Chip
                  className="resource-detail__category-chip"
                  icon={<Category />}
                  label={resource.category}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip
                className="resource-detail__published-chip"
                icon={<CalendarToday />}
                label={`Published ${formatDate(resource.publishedDate)}`}
                variant="outlined"
              />
              {resource.updatedDate !== resource.publishedDate && (
                <Chip
                  className="resource-detail__updated-chip"
                  icon={<CalendarToday />}
                  label={`Updated ${formatDate(resource.updatedDate)}`}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Box>

            {/* Tags */}
            {resource.tags.length > 0 && (
              <Box className="resource-detail__tags" mb={3}>
                <Typography className="resource-detail__tags-label" variant="subtitle2" gutterBottom>
                  Tags:
                </Typography>
                <Box className="resource-detail__tags-list" display="flex" flexWrap="wrap" gap={1}>
                  {resource.tags.map((tag) => (
                    <Chip
                      className="resource-detail__tag-chip"
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Featured Image */}
          {resource.featuredImage && (
            <Box className="resource-detail__featured-image" mb={4}>
              <img
                className="resource-detail__image"
                src={resource.featuredImage.url.startsWith('http') 
                  ? resource.featuredImage.url 
                  : `https:${resource.featuredImage.url}`}
                alt={resource.featuredImage.alt}
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: 8,
                  maxHeight: '400px',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}

          <Divider className="resource-detail__divider" sx={{ mb: 4 }} />

          {/* Content */}
          <Box
            className="resource-detail__body"
            dangerouslySetInnerHTML={{ __html: resource.content }}
            sx={{
              '& p': { 
                mb: 2, 
                lineHeight: 1.7,
                fontSize: '1.1rem'
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                mt: 4, 
                mb: 2,
                fontWeight: 600,
                color: 'text.primary'
              },
              '& h1': { fontSize: '2rem' },
              '& h2': { fontSize: '1.75rem' },
              '& h3': { fontSize: '1.5rem' },
              '& h4': { fontSize: '1.25rem' },
              '& ul, & ol': { 
                pl: 3, 
                mb: 2,
                '& li': {
                  mb: 1,
                  lineHeight: 1.6
                }
              },
              '& img': { 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: 1,
                my: 2
              },
              '& blockquote': {
                borderLeft: 4,
                borderColor: 'primary.main',
                pl: 2,
                py: 1,
                my: 2,
                bgcolor: 'grey.50',
                fontStyle: 'italic'
              },
              '& code': {
                bgcolor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontFamily: 'monospace'
              },
              '& pre': {
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                '& code': {
                  bgcolor: 'transparent',
                  p: 0
                }
              }
            }}
          />

          {/* Footer */}
          <Divider className="resource-detail__footer-divider" sx={{ my: 4 }} />
          
          <Box className="resource-detail__footer" display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2}>
            <Box className="resource-detail__footer-dates" textAlign="right">
              <Typography className="resource-detail__published-date" variant="caption" color="text.secondary" display="block">
                Published: {formatDate(resource.publishedDate)}
              </Typography>
              {resource.updatedDate !== resource.publishedDate && (
                <Typography className="resource-detail__updated-date" variant="caption" color="text.secondary" display="block">
                  Last updated: {formatDate(resource.updatedDate)}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {recommendedResources.length > 0 && (
          <Box className="resource-detail__recommended" mt={8}>
            <Typography className="resource-detail__recommended-title" variant="h5" gutterBottom>
              Recommended reading
            </Typography>
            <Typography className="resource-detail__recommended-subtitle" variant="body2" color="text.secondary" gutterBottom>
              Explore more insights and guides from our library.
            </Typography>
            <Grid className="resource-detail__recommended-grid" container spacing={3} mt={1}>
              {recommendedResources.map((item) => (
                <Grid
                  key={item.id}
                  item
                  xs={12}
                  md={4}
                  className="resource-detail__recommended-item"
                >
                  <Paper
                    elevation={0}
                    className="resource-detail__recommended-card"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: '1px solid #e5e7eb',
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: 3,
                        borderColor: '#0043FE',
                      },
                    }}
                    onClick={() => navigate(`/resources/${item.id}`)}
                  >
                    <Typography
                      className="resource-detail__recommended-card-title"
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      className="resource-detail__recommended-card-description"
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      {item.description}
                    </Typography>
                    <Typography
                      className="resource-detail__recommended-card-date"
                      variant="caption"
                      color="text.secondary"
                    >
                      Published {formatDate(item.publishedDate)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      {/* Site footer (shared with other public pages) */}
      <Box className="site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        {/* Middle nav columns */}
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography className="site-footer__branddesc" variant="body2" sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}>
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
          </Grid>
        </Container>

        {/* Bottom bar */}
        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box className="site-footer__bar-inner" sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}>
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">© {new Date().getFullYear()} SuperGauge</Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">Contact</Typography>
                  <Typography className="site-footer__link" variant="caption">Terms</Typography>
                  <Typography className="site-footer__link" variant="caption">Privacy policy</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ResourceDetail;
